import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export interface ExportPdfInput {
  /** Element holding the rendered preview HTML — typically an iframe's contentDocument.body */
  source: HTMLElement;
  paperSize: 'A4' | 'A5' | 'A3' | 'Letter';
  orientation: 'portrait' | 'landscape';
  marginMm: number;
  fileName: string;
}

const PAPER_MM: Record<ExportPdfInput['paperSize'], [number, number]> = {
  A4: [210, 297],
  A5: [148, 210],
  A3: [297, 420],
  Letter: [216, 279],
};

/**
 * 批量导出：逐条渲染再追加进同一个 PDF。
 *
 * 以前是把上百条记录拼成一张长 HTML 再 html2canvas 一次 —— 画布高度超过
 * 浏览器上限（Chrome 单边 32767px，约十来页）后直接返回空白画布，用户拿到
 * 一份全是白页的 PDF，还提示「导出成功」。
 */
export async function exportManyToPdf(input: {
  /** 逐条产出 DOM：调用方负责离屏渲染与清理。 */
  next: (index: number) => Promise<{ body: HTMLElement; cleanup: () => void } | null>;
  count: number;
  paperSize: ExportPdfInput['paperSize'];
  orientation: ExportPdfInput['orientation'];
  marginMm: number;
  fileName: string;
  onProgress?: (done: number, total: number) => void;
}): Promise<void> {
  const [pw, ph] = PAPER_MM[input.paperSize];
  const pageWidthMm = input.orientation === 'portrait' ? pw : ph;
  const pageHeightMm = input.orientation === 'portrait' ? ph : pw;
  const pdf = new jsPDF({
    unit: 'mm',
    format: [pageWidthMm, pageHeightMm],
    orientation: input.orientation === 'portrait' ? 'p' : 'l',
    compress: true,
  });
  let first = true;
  for (let i = 0; i < input.count; i++) {
    const rendered = await input.next(i);
    if (!rendered) continue;
    try {
      first = await appendSourceToPdf(pdf, rendered.body, {
        pageWidthMm,
        pageHeightMm,
        orientation: input.orientation,
        marginMm: input.marginMm,
        first,
      });
    } finally {
      rendered.cleanup();
    }
    input.onProgress?.(i + 1, input.count);
  }
  pdf.save(input.fileName);
}

/** 按纸张宽度重排克隆文档；分页时上下留白由 PDF 每页补回，左右只保留一次。 */
function captureSource(
  source: HTMLElement,
  pageWidthMm: number,
  marginMm: number,
  continuous = false,
): Promise<HTMLCanvasElement> {
  const widthPx = pageWidthMm * 96 / 25.4;
  return html2canvas(source, {
    backgroundColor: '#ffffff',
    scale: window.devicePixelRatio > 1 ? 2 : 1.5,
    useCORS: true,
    logging: false,
    // html2canvas 会向下取整画布宽度。向上取整 CSS 像素，避免缩小像素/mm
    // 比例后把恰好装满的文档切出一条尾页（0 边距时尤其明显）。
    width: Math.ceil(widthPx),
    windowWidth: Math.ceil(widthPx),
    // body 截图默认至少和视口一样高。离屏 iframe 的 1200px 高度曾让一页
    // A4/A5 单据多出白页；让克隆文档按实际内容高度撑开。
    windowHeight: 1,
    scrollX: 0,
    scrollY: 0,
    onclone: (doc, body) => {
      doc.documentElement.style.width = `${pageWidthMm}mm`;
      doc.documentElement.style.minHeight = '0';
      doc.documentElement.style.height = 'auto';
      Object.assign(body.style, {
        boxSizing: 'border-box',
        width: `${pageWidthMm}mm`,
        minHeight: '0',
        height: 'auto',
        margin: '0',
        padding: `${continuous ? marginMm : 0}mm ${marginMm}mm`,
      });
    },
  });
}

/** 只切正文高度，每一页都留上下边距。单条、批量和存附件共用。 */
async function appendSourceToPdf(
  pdf: jsPDF,
  source: HTMLElement,
  o: {
    pageWidthMm: number;
    pageHeightMm: number;
    orientation: ExportPdfInput['orientation'];
    marginMm: number;
    first: boolean;
  },
): Promise<boolean> {
  const canvas = await captureSource(source, o.pageWidthMm, o.marginMm);
  const pxPerMm = canvas.width / o.pageWidthMm;
  const sliceHeightPx = Math.round((o.pageHeightMm - 2 * o.marginMm) * pxPerMm);
  if (canvas.width <= 0 || canvas.height <= 0 || sliceHeightPx <= 0) {
    throw new Error('无法生成 PDF，请检查纸张尺寸和边距设置');
  }
  let yOffset = 0;
  let first = o.first;
  while (yOffset < canvas.height) {
    // CSS 毫米转画布像素会取整；末尾仅多 1px 时并入本页，避免一张白页。
    const remaining = canvas.height - yOffset;
    const thisSlice = remaining <= sliceHeightPx + 1 ? remaining : sliceHeightPx;
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = thisSlice;
    const ctx = slice.getContext('2d');
    if (!ctx) throw new Error('无法创建 PDF 画布');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, yOffset, canvas.width, thisSlice, 0, 0, canvas.width, thisSlice);
    if (!first) {
      pdf.addPage([o.pageWidthMm, o.pageHeightMm], o.orientation === 'portrait' ? 'p' : 'l');
    }
    first = false;
    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      0,
      o.marginMm,
      o.pageWidthMm,
      thisSlice / pxPerMm,
    );
    yOffset += thisSlice;
  }
  return first;
}

/**
 * 同 exportToPdf，但把结果作为 Blob 交出来而不是直接下载。
 * 「存附件」要拿到文件本体才能上传，不能走浏览器下载那条路。
 */
export async function buildPdfBlob(
  input: ExportPdfInput & { continuous?: boolean },
): Promise<Blob> {
  let blob: Blob | null = null;
  await exportToPdf({ ...input, __emit: (b: Blob) => { blob = b; } } as never);
  if (!blob) throw new Error('生成 PDF 失败');
  return blob;
}

export async function exportToPdf(input: ExportPdfInput & { continuous?: boolean }): Promise<void> {
  const { source, paperSize, orientation, marginMm, fileName, continuous } = input;
  // 内部钩子：给 buildPdfBlob 用。有它就把结果交出去而不触发下载。
  const emit = (input as unknown as { __emit?: (b: Blob) => void }).__emit;
  const finish = (pdf: jsPDF) => {
    if (emit) emit(pdf.output('blob'));
    else pdf.save(fileName);
  };

  const [pw, ph] = PAPER_MM[paperSize];
  const pageWidthMm = orientation === 'portrait' ? pw : ph;
  const pageHeightMm = orientation === 'portrait' ? ph : pw;
  // 连续页面：整份内容成一长页，不分页
  if (continuous) {
    const canvas = await captureSource(source, pageWidthMm, marginMm, true);
    const fullHeightMm = canvas.height / (canvas.width / pageWidthMm);
    const pdf = new jsPDF({
      unit: 'mm',
      format: [pageWidthMm, fullHeightMm],
      orientation: pageWidthMm >= fullHeightMm ? 'l' : 'p',
      compress: true,
    });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageWidthMm, fullHeightMm);
    finish(pdf);
    return;
  }

  const pdf = new jsPDF({
    unit: 'mm',
    format: [pageWidthMm, pageHeightMm],
    orientation: orientation === 'portrait' ? 'p' : 'l',
    compress: true,
  });

  await appendSourceToPdf(pdf, source, {
    pageWidthMm, pageHeightMm, orientation, marginMm, first: true,
  });

  finish(pdf);
}

export function buildPdfFileName(tableName: string, now: Date = new Date()): string {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}_` +
    `${pad2(now.getHours())}${pad2(now.getMinutes())}`;
  const safe = (tableName || '导出').replace(/[\\/:*?"<>|]/g, '_').slice(0, 40);
  return `${safe}_${stamp}.pdf`;
}
